/**
 * HTML Template Generator
 * Generates the HTML structure for each template type based on configuration
 */

import { TemplateType, TemplateConfig } from './types';
import { generateRegistryHtml, generateRegistryPreviewHtml } from './templateRegistry/adapters';

/**
 * Generate HTML template string for a given template and config
 */
function injectCustomCss(html: string, config: TemplateConfig): string {
  const customCss = (config.customCss as string) || '';
  if (!customCss) return html;
  // If HTML has a <style> block, inject before closing </style>
  if (html.includes('</style>')) {
    return html.replace('</style>', `\n${customCss}\n</style>`);
  }
  // Otherwise prepend a style block
  return `<style>${customCss}</style>${html}`;
}

export function generateHtmlTemplate(templateId: TemplateType, config: TemplateConfig): string {
  return injectCustomCss(_generateHtmlTemplate(templateId, config), config);
}

function _generateHtmlTemplate(templateId: TemplateType, config: TemplateConfig): string {
  // Check new registry first (plug-and-play templates)
  const registryHtml = generateRegistryHtml(templateId, config);
  if (registryHtml) return registryHtml;

  // Legacy templates - use switch statement
  const c = config;

  switch (templateId) {
    case 'vertical-link-list':
      return `<div class="quattr-module quattr-link-list${
        c.customCssClass ? ' ' + c.customCssClass : ''
      }" data-module-id="{{moduleId}}">
  <${c.headingTag} class="quattr-module-heading">${c.moduleHeading}</${c.headingTag}>
  <ul class="quattr-links-container" data-list-style="${c.listStyle}" data-spacing="${c.itemSpacing}">
    {{#each links}}
    <li class="quattr-link-item">
      <a href="{{url}}" class="quattr-link">{{anchorText}}</a>
    </li>
    {{/each}}
  </ul>
  ${c.showViewMore ? `<a href="{{viewMoreUrl}}" class="quattr-view-more">${c.viewMoreText}</a>` : ''}
</div>`;

    case 'multi-column-links':
      return `<div class="quattr-module quattr-multi-column${
        c.customCssClass ? ' ' + c.customCssClass : ''
      }" data-module-id="{{moduleId}}" data-columns="${c.columns}">
  ${c.sectionHeading ? `<h2 class="quattr-section-heading">${c.sectionHeading}</h2>` : ''}
  <div class="quattr-columns-container" data-responsive="${c.responsiveBehavior}" data-background="${
        c.containerBackground
      }">
    {{#each modules}}
    <div class="quattr-column" data-divider="${c.columnDivider}">
      <h3 class="quattr-column-heading">{{heading}}</h3>
      <ul class="quattr-column-links">
        {{#each links}}
        <li><a href="{{url}}">{{anchorText}}</a></li>
        {{/each}}
      </ul>
      ${c.showViewMore ? '<a href="{{viewMoreUrl}}" class="quattr-view-more">View More</a>' : ''}
    </div>
    {{/each}}
  </div>
</div>`;

    case 'image-card-grid':
      return `<div class="quattr-module quattr-card-grid${
        c.customCssClass ? ' ' + c.customCssClass : ''
      }" data-module-id="{{moduleId}}">
  <${c.headingTag} class="quattr-module-heading">${c.moduleHeading}</${c.headingTag}>
  <div class="quattr-cards-container" data-columns="${c.gridColumns}" data-style="${c.cardStyle}" data-hover="${
        c.hoverEffect
      }">
    {{#each links}}
    <a href="{{url}}" class="quattr-card" data-radius="${c.cardBorderRadius}">
      <div class="quattr-card-image" data-aspect="${c.imageAspectRatio}">
        <img src="{{image}}" alt="{{title}}" loading="lazy">
      </div>
      ${
        c.showTitle
          ? `<div class="quattr-card-content" data-position="${c.titlePosition}">
        <span class="quattr-card-title">{{title}}</span>
        ${c.showDescription ? '<p class="quattr-card-description">{{description}}</p>' : ''}
      </div>`
          : ''
      }
    </a>
    {{/each}}
  </div>
</div>`;

    case 'rich-article-cards':
      return `<div class="quattr-module quattr-article-cards${
        c.customCssClass ? ' ' + c.customCssClass : ''
      }" data-module-id="{{moduleId}}" data-layout="${c.layout}">
  <h2 class="quattr-module-heading">${c.moduleHeading}</h2>
  <div class="quattr-articles-container" data-columns="${c.gridColumns}">
    {{#each links}}
    <article class="quattr-article-card">
      ${
        c.showImage
          ? `<a href="{{url}}" class="quattr-article-image" data-aspect="${c.imageAspectRatio}">
        <img src="{{image}}" alt="{{title}}" loading="lazy">
      </a>`
          : ''
      }
      <div class="quattr-article-content">
        ${c.showCategory ? '<span class="quattr-article-category">{{category}}</span>' : ''}
        ${c.showReadingTime ? '<span class="quattr-article-reading-time">{{readingTime}}</span>' : ''}
        <a href="{{url}}" class="quattr-article-title">{{title}}</a>
        ${c.showExcerpt ? '<p class="quattr-article-excerpt">{{excerpt}}</p>' : ''}
        ${c.showDate ? '<time class="quattr-article-date" datetime="{{publishDate}}">{{publishDate}}</time>' : ''}
      </div>
    </article>
    {{/each}}
  </div>
</div>`;

    case 'image-carousel':
      return `<div class="quattr-module quattr-carousel${
        c.customCssClass ? ' ' + c.customCssClass : ''
      }" data-module-id="{{moduleId}}">
  <div class="quattr-carousel-header">
    <h2 class="quattr-module-heading">${c.moduleHeading}</h2>
    ${
      c.showArrows
        ? `<div class="quattr-carousel-nav">
      <button class="quattr-carousel-prev" aria-label="Previous">←</button>
      <button class="quattr-carousel-next" aria-label="Next">→</button>
    </div>`
        : ''
    }
  </div>
  <div class="quattr-carousel-track" data-visible="${c.visibleSlides}" data-gap="${c.gap}" ${
        c.autoPlay ? 'data-autoplay="true"' : ''
      }>
    {{#each links}}
    <a href="{{url}}" class="quattr-carousel-slide" data-style="${c.cardStyle}">
      <div class="quattr-slide-image" data-aspect="${c.imageAspectRatio}">
        <img src="{{image}}" alt="{{title}}" loading="lazy">
      </div>
      ${c.cardStyle !== 'image-only' ? '<span class="quattr-slide-title">{{title}}</span>' : ''}
    </a>
    {{/each}}
  </div>
  ${c.showDots ? '<div class="quattr-carousel-dots"></div>' : ''}
</div>`;

    case 'sidebar-link-box':
      return `<aside class="quattr-module quattr-sidebar-box${
        c.customCssClass ? ' ' + c.customCssClass : ''
      }" data-module-id="{{moduleId}}" data-style="${c.containerStyle}">
  <h3 class="quattr-sidebar-heading" data-heading-style="${c.headingStyle}">${c.moduleHeading}</h3>
  <ul class="quattr-sidebar-links" data-link-style="${c.linkStyle}" ${c.showDividers ? 'data-dividers="true"' : ''}>
    {{#each links}}
    <li class="quattr-sidebar-item">
      ${c.linkStyle === 'numbered' ? '<span class="quattr-item-number">{{@index}}</span>' : ''}
      <a href="{{url}}">{{anchorText}}</a>
    </li>
    {{/each}}
  </ul>
  ${c.expandable ? `<button class="quattr-expand-toggle" data-initial="${c.initialVisible}">Show More</button>` : ''}
</aside>`;

    case 'sidebar-card-stack':
      return `<aside class="quattr-module quattr-sidebar-cards${
        c.customCssClass ? ' ' + c.customCssClass : ''
      }" data-module-id="{{moduleId}}">
  <h3 class="quattr-sidebar-heading">${c.moduleHeading}</h3>
  <div class="quattr-sidebar-stack" data-image-position="${c.imagePosition}">
    {{#each links}}
    <a href="{{url}}" class="quattr-sidebar-card" data-bg="${c.cardBackground}">
      ${
        c.showImage
          ? `<div class="quattr-card-thumb" data-size="${c.imageSize}">
        <img src="{{thumbnail}}" alt="{{title}}" loading="lazy">
      </div>`
          : ''
      }
      <div class="quattr-card-info">
        ${c.showCategory ? '<span class="quattr-card-category">{{category}}</span>' : ''}
        <span class="quattr-card-title">{{title}}</span>
      </div>
    </a>
    {{/each}}
  </div>
</aside>`;

    case 'inline-reading-callout':
      return `<div class="quattr-module quattr-inline-callout${
        c.customCssClass ? ' ' + c.customCssClass : ''
      }" data-module-id="{{moduleId}}" data-style="${c.style}" data-float="${c.floatPosition}" data-width="${c.width}">
  <span class="quattr-callout-heading">${c.showIcon ? '📚 ' : ''}${c.calloutHeading}</span>
  <ul class="quattr-callout-links" data-display="${c.linkDisplay}">
    {{#each links}}
    <li><a href="{{url}}">{{anchorText}}</a></li>
    {{/each}}
  </ul>
</div>`;

    case 'contextual-link-box': {
      const headingSrc = c.headingSource || 'anchorText';
      const headingTpl =
        headingSrc === 'custom'
          ? c.heading || 'Dive Deeper'
          : headingSrc === 'title'
          ? '{{title}}'
          : headingSrc === 'h1'
          ? '{{h1}}'
          : '{{anchorText}}';
      const ctaPos = c.ctaPosition || 'left';
      const ctaAlign = ctaPos === 'right' ? 'text-align:right;' : ctaPos === 'below' ? 'margin-top:0.5rem;' : '';
      const maxBoxes = parseInt(c.maxBoxes as string) || 6;
      const expandable = c.expandable;
      const initialVisible = parseInt(c.initialVisible as string) || 3;
      return `<div class="quattr-module quattr-contextual-box-wrap${
        c.customCssClass ? ' ' + c.customCssClass : ''
      }" data-module-id="{{moduleId}}" data-init="${initialVisible}">
  {{#each links}}
  {{#if (lt @index ${maxBoxes})}}
  <div class="quattr-contextual-box${expandable && '@index' ? '' : ''}" data-border="${c.borderStyle}"${
        expandable ? ` data-box-index="{{@index}}"` : ''
      }>
    <div class="quattr-box-content" data-image-position="${c.imagePosition}">
      <div class="quattr-box-text">
        <span class="quattr-box-heading">${c.showIcon ? '📖 ' : ''}${headingTpl}</span>
        ${c.showDescription ? '<p class="quattr-box-description">{{description}}</p>' : ''}
        <div class="quattr-box-cta-wrap" style="${ctaAlign}">
          <a href="{{url}}" class="quattr-box-cta" data-style="${c.ctaStyle}">${c.ctaText} →</a>
        </div>
      </div>
      ${c.showImage ? '<div class="quattr-box-image"><img src="{{image}}" alt="{{title}}" loading="lazy"></div>' : ''}
    </div>
  </div>
  {{/if}}
  {{/each}}
  ${
    expandable
      ? `<a href="#" class="quattr-clb-show-more" data-expanded="false">${c.viewMoreText || 'Show More'}</a>`
      : ''
  }
</div>`;
    }

    case 'related-searches': {
      const pillRadius = c.borderRadius === 'pill' ? '50px' : c.borderRadius === 'square' ? '4px' : '8px';
      const pillPadding = c.pillSize === 'small' ? '6px 12px' : c.pillSize === 'large' ? '12px 20px' : '8px 16px';
      const pillFont = c.pillSize === 'small' ? '12px' : c.pillSize === 'large' ? '15px' : '13px';
      const pillColor = c.pillColor || '#2563eb';
      const textColorVal = c.textColor || '#ffffff';
      const hoverStyle = c.hoverStyle || 'darken';
      const pillsPerRow = c.pillsPerRow || 5;
      const isExpandable = Boolean(c.expandable);
      const viewMoreText = c.viewMoreText || 'Show More';

      let pillBg, pillTextColor, pillBorder;
      if (c.pillStyle === 'filled') {
        pillBg = pillColor;
        pillTextColor = textColorVal;
        pillBorder = 'none';
      } else if (c.pillStyle === 'outlined') {
        pillBg = 'transparent';
        pillTextColor = pillColor;
        pillBorder = `1.5px solid ${pillColor}`;
      } else {
        pillBg = 'transparent';
        pillTextColor = pillColor;
        pillBorder = 'none';
      }

      // Generate hover CSS based on hoverStyle
      let hoverCss = '';
      if (hoverStyle === 'darken') {
        if (c.pillStyle === 'filled') {
          hoverCss = `background:color-mix(in srgb, ${pillColor} 80%, black);`;
        } else {
          hoverCss = `background:color-mix(in srgb, ${pillColor} 80%, black);color:${textColorVal};`;
        }
      } else if (hoverStyle === 'lighten') {
        if (c.pillStyle === 'filled') {
          hoverCss = `background:color-mix(in srgb, ${pillColor} 80%, white);`;
        } else {
          hoverCss = `background:${pillColor}15;`;
        }
      } else if (hoverStyle === 'invert') {
        if (c.pillStyle === 'filled') {
          hoverCss = `background:transparent;color:${pillColor};border:1.5px solid ${pillColor};`;
        } else {
          hoverCss = `background:${pillColor};color:${textColorVal};border:none;`;
        }
      }

      const layoutStyle =
        c.layout === 'scroll'
          ? 'display:flex;gap:8px;overflow-x:auto;'
          : c.layout === 'grid'
          ? `display:grid;grid-template-columns:repeat(${c.columns || 3},1fr);gap:8px;`
          : 'display:flex;flex-wrap:wrap;gap:8px;';

      const moduleId = '{{moduleId}}';
      const expandableScript = isExpandable
        ? `
<script>
(function(){
  var container = document.querySelector('[data-module-id="${moduleId}"]');
  if (!container) return;
  var pills = container.querySelectorAll('.quattr-pill');
  var viewMoreBtn = container.querySelector('.quattr-view-more-btn');
  var expanded = false;
  var pillsPerRow = ${pillsPerRow};

  function updateVisibility() {
    pills.forEach(function(pill, i) {
      pill.style.display = (expanded || i < pillsPerRow) ? '' : 'none';
    });
    if (viewMoreBtn) {
      viewMoreBtn.textContent = expanded ? '← Show Less' : '${viewMoreText}';
    }
  }

  if (viewMoreBtn) {
    viewMoreBtn.addEventListener('click', function(e) {
      e.preventDefault();
      expanded = !expanded;
      updateVisibility();
    });
  }
  updateVisibility();
})();
</script>`
        : '';

      const customCss = (c.customCss as string) || '';
      return `<style>
.quattr-related-searches .quattr-pill:hover { ${hoverCss}text-decoration:none !important; }
${customCss}
</style>
<div class="quattr-module quattr-related-searches${
        c.customCssClass ? ' ' + c.customCssClass : ''
      }" data-module-id="{{moduleId}}" style="background:${c.backgroundColor || '#f8fafc'};padding:${
        c.containerPadding === 'custom' && c.customPadding
          ? c.customPadding
          : c.containerPadding === 'none'
          ? '0'
          : c.containerPadding === 'sm'
          ? '0.5rem'
          : c.containerPadding === 'lg'
          ? '1.5rem'
          : '1rem'
      };font-family:system-ui,-apple-system,sans-serif;border-radius:8px;">
  ${
    c.showHeading
      ? `<div style="display:flex;align-items:center;gap:8px;margin-bottom:14px;">
    ${
      c.headingStyle === 'with-icon'
        ? '<svg style="width:18px;height:18px;color:#666;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>'
        : ''
    }
    <h4 style="margin:0;flex:1;font-size:${c.headingStyle === 'minimal' ? '13px' : '15px'};font-weight:${
          c.headingStyle === 'minimal' ? '500' : '600'
        };color:${c.headingStyle === 'minimal' ? '#666' : '#1a1a2e'};${
          c.headingStyle === 'minimal' ? 'text-transform:uppercase;letter-spacing:0.5px;' : ''
        }">${c.headingText || 'Related Searches'}</h4>
    ${
      isExpandable
        ? `<a href="#" class="quattr-view-more-btn" style="font-size:13px;color:${pillColor};text-decoration:none;font-weight:500;">${viewMoreText}</a>`
        : ''
    }
  </div>`
      : ''
  }
  <div style="${layoutStyle}">
    {{#each links}}
    {{#if (lt @index ${c.maxPills || 10})}}
    <a href="{{url}}" class="quattr-pill" style="display:inline-flex;align-items:center;gap:6px;padding:${pillPadding};border-radius:${pillRadius};font-size:${pillFont};font-weight:500;text-decoration:none;white-space:nowrap;background:${pillBg};color:${pillTextColor};border:${pillBorder};transition:all 0.2s ease;">
      ${
        c.showSearchIcon
          ? '<svg style="width:14px;height:14px;flex-shrink:0;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>'
          : ''
      }
      <span>{{title}}</span>
    </a>
    {{/if}}
    {{/each}}
  </div>
</div>${expandableScript}`;
    }

    default:
      return '<!-- Template HTML will be generated here -->';
  }
}

/**
 * Generate preview HTML (with sample data filled in) for cloud storage
 */
export function generatePreviewHtml(templateId: TemplateType, config: TemplateConfig): string {
  return injectCustomCss(_generatePreviewHtml(templateId, config), config);
}

function _generatePreviewHtml(templateId: TemplateType, config: TemplateConfig): string {
  // Check new registry first (plug-and-play templates)
  const registryPreviewHtml = generateRegistryPreviewHtml(templateId, config);
  if (registryPreviewHtml) return registryPreviewHtml;

  // Legacy templates - use switch statement
  // This generates actual HTML with sample data for the bookmarklet preview
  // The implementation uses the same logic as TemplatePreviewRenderer but outputs HTML string
  const c = config;

  const GRADIENTS = ['#3ecf8e,#14b8a6', '#f59e0b,#ef4444', '#8b5cf6,#ec4899', '#06b6d4,#3b82f6', '#10b981,#059669'];

  const getGradient = (i: number) => `linear-gradient(135deg, ${GRADIENTS[i % GRADIENTS.length]})`;

  const SAMPLE_LINKS = [
    'Getting Started Guide',
    'Best Practices for SEO',
    'Advanced Configuration Tips',
    'Troubleshooting Issues',
    'Performance Optimization',
    'API Integration Guide',
    'Security Best Practices',
    'Deployment Strategies',
    'Monitoring and Analytics',
    'User Management Guide',
  ];
  const SAMPLE_PRODUCTS = [
    'Product A',
    'Product B',
    'Product C',
    'Product D',
    'Product E',
    'Product F',
    'Product G',
    'Product H',
  ];

  switch (templateId) {
    case 'vertical-link-list': {
      const maxLinks = (c.maxLinks as number) || 8;
      const hasMoreLinks = SAMPLE_LINKS.length > maxLinks;
      const moduleId = `preview-vll-${Date.now()}`;
      const viewMoreText = (c.viewMoreText as string) || 'View More';

      const linksHtml = SAMPLE_LINKS.map(
        (text, idx) =>
          `<li class="quattr-link-item${idx >= maxLinks ? ' quattr-hidden' : ''}" style="padding:0.4rem 0;${
            idx >= maxLinks ? 'display:none;' : ''
          }"><a href="#" style="color:#2db77a;text-decoration:none;">${text}</a></li>`
      ).join('');

      const viewMoreHtml =
        c.showViewMore && hasMoreLinks
          ? `<div style="margin-top:0.5rem;"><a href="javascript:void(0)" class="quattr-view-more" data-expanded="false" style="color:#64748b;text-decoration:none;cursor:pointer;">${viewMoreText} →</a></div>`
          : '';

      const scriptHtml =
        c.showViewMore && hasMoreLinks
          ? `<script>(function(){var m=document.querySelector('[data-module-id="${moduleId}"]');if(!m)return;var btn=m.querySelector('.quattr-view-more');var items=m.querySelectorAll('.quattr-link-item');var maxLinks=${maxLinks};btn.addEventListener('click',function(e){e.preventDefault();var expanded=btn.getAttribute('data-expanded')==='true';items.forEach(function(item,i){if(i>=maxLinks){item.style.display=expanded?'none':'';item.classList.toggle('quattr-hidden',expanded);}});btn.setAttribute('data-expanded',expanded?'false':'true');btn.textContent=expanded?'${viewMoreText} →':'← Show Less';});})();<\/script>`
          : '';

      return `
        <div style="padding:1rem;" data-module-id="${moduleId}">
          <h3 style="color:#1e3a5f;margin-bottom:0.75rem;">${c.moduleHeading}</h3>
          <ul style="list-style:${
            c.listStyle === 'bullets' ? 'disc' : c.listStyle === 'numbered' ? 'decimal' : 'none'
          };padding-left:${c.listStyle !== 'none' ? '1.5rem' : '0'};margin:0;">
            ${linksHtml}
          </ul>
          ${viewMoreHtml}
        </div>${scriptHtml}`;
    }

    case 'image-card-grid': {
      const cardCount = Math.min((c.maxCards as number) || 8, 4);
      const cols = c.gridColumns === 'auto-fit' ? 3 : parseInt(c.gridColumns as string);
      return `
        <div style="padding:1rem;">
          <h2 style="color:#1e3a5f;margin-bottom:0.75rem;">${c.moduleHeading}</h2>
          <div style="display:grid;grid-template-columns:repeat(${Math.min(cols, 4)},1fr);gap:1rem;">
            ${SAMPLE_PRODUCTS.slice(0, cardCount)
              .map(
                (title, i) => `
              <div style="border-radius:8px;overflow:hidden;box-shadow:0 4px 12px rgba(0,0,0,0.15);background:white;">
                <div style="height:120px;background:${getGradient(i)};"></div>
                ${c.showTitle ? `<div style="padding:0.75rem;font-weight:500;color:#1e3a5f;">${title}</div>` : ''}
              </div>
            `
              )
              .join('')}
          </div>
        </div>`;
    }

    case 'related-searches': {
      const SAMPLE_SEARCHES = [
        'mens suits',
        'mens black suits',
        'mens khakis',
        'mens dress shirts',
        'mens blazers',
        'mens ties',
        'formal wear',
        'business casual',
        'slim fit suits',
        'wedding suits',
      ];
      const maxPills = Math.min((c.maxPills as number) || 10, 10);
      const pillsPerRow = (c.pillsPerRow as number) || 5;
      const isExpandable = Boolean(c.expandable);
      const viewMoreText = (c.viewMoreText as string) || 'Show More';
      const pillRadius = c.borderRadius === 'pill' ? '50px' : c.borderRadius === 'square' ? '4px' : '8px';
      const pillPadding = c.pillSize === 'small' ? '6px 12px' : c.pillSize === 'large' ? '12px 20px' : '8px 16px';
      const pillFont = c.pillSize === 'small' ? '12px' : c.pillSize === 'large' ? '15px' : '13px';
      const pillColor = (c.pillColor as string) || '#2563eb';
      const textColorVal = (c.textColor as string) || '#ffffff';
      const hoverStyle = (c.hoverStyle as string) || 'darken';
      const moduleId = `preview-rs-${Date.now()}`;

      let pillBg, pillTextColor, pillBorder;
      if (c.pillStyle === 'filled') {
        pillBg = pillColor;
        pillTextColor = textColorVal;
        pillBorder = 'none';
      } else if (c.pillStyle === 'outlined') {
        pillBg = 'transparent';
        pillTextColor = pillColor;
        pillBorder = `1.5px solid ${pillColor}`;
      } else {
        pillBg = 'transparent';
        pillTextColor = pillColor;
        pillBorder = 'none';
      }

      // Generate hover CSS
      let hoverCss = '';
      if (hoverStyle === 'darken') {
        hoverCss =
          c.pillStyle === 'filled' ? `filter:brightness(0.85);` : `background:${pillColor};color:${textColorVal};`;
      } else if (hoverStyle === 'lighten') {
        hoverCss = c.pillStyle === 'filled' ? `filter:brightness(1.15);` : `background:${pillColor}15;`;
      } else if (hoverStyle === 'invert') {
        hoverCss =
          c.pillStyle === 'filled'
            ? `background:transparent;color:${pillColor};border:1.5px solid ${pillColor};`
            : `background:${pillColor};color:${textColorVal};border:none;`;
      }

      const layoutStyle =
        c.layout === 'scroll'
          ? 'display:flex;gap:8px;overflow-x:auto;'
          : c.layout === 'grid'
          ? `display:grid;grid-template-columns:repeat(${c.columns || 3},1fr);gap:8px;`
          : 'display:flex;flex-wrap:wrap;gap:8px;';

      const searchIcon = c.showSearchIcon
        ? '<svg style="width:14px;height:14px;flex-shrink:0;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>'
        : '';

      // For preview with expandable, render all pills but hide those beyond pillsPerRow
      const hasMorePills = SAMPLE_SEARCHES.length > pillsPerRow;
      const showExpandButton = isExpandable && hasMorePills;

      const pillsHtml = SAMPLE_SEARCHES.slice(0, maxPills)
        .map(
          (term, idx) => `
          <a href="#" class="preview-pill quattr-pill${
            isExpandable && idx >= pillsPerRow ? ' quattr-hidden' : ''
          }" style="display:${
            isExpandable && idx >= pillsPerRow ? 'none' : 'inline-flex'
          };align-items:center;gap:6px;padding:${pillPadding};border-radius:${pillRadius};font-size:${pillFont};font-weight:500;text-decoration:none;white-space:nowrap;background:${pillBg};color:${pillTextColor};border:${pillBorder};transition:all 0.2s ease;">
            ${searchIcon}
            <span>${term}</span>
          </a>
        `
        )
        .join('');

      const expandScript = showExpandButton
        ? `<script>(function(){var m=document.querySelector('[data-module-id="${moduleId}"]');if(!m)return;var btn=m.querySelector('.quattr-expand-btn');var pills=m.querySelectorAll('.quattr-pill');var pillsPerRow=${pillsPerRow};btn.addEventListener('click',function(e){e.preventDefault();var expanded=btn.getAttribute('data-expanded')==='true';pills.forEach(function(pill,i){if(i>=pillsPerRow){pill.style.display=expanded?'none':'inline-flex';pill.classList.toggle('quattr-hidden',expanded);}});btn.setAttribute('data-expanded',expanded?'false':'true');btn.textContent=expanded?'${viewMoreText}':'← Show Less';});})();<\/script>`
        : '';

      const customCssPreview = (c.customCss as string) || '';
      return `
        <style>.preview-pill:hover { ${hoverCss}text-decoration:none !important; }${
        customCssPreview ? '\n' + customCssPreview : ''
      }</style>
        <div style="background:${c.backgroundColor || '#f8fafc'};padding:${
        c.containerPadding === 'custom' && c.customPadding
          ? c.customPadding
          : c.containerPadding === 'none'
          ? '0'
          : c.containerPadding === 'sm'
          ? '0.5rem'
          : c.containerPadding === 'lg'
          ? '1.5rem'
          : '1rem'
      };font-family:system-ui,-apple-system,sans-serif;border-radius:8px;" data-module-id="${moduleId}">
          ${
            c.showHeading
              ? `<div style="display:flex;align-items:center;gap:8px;margin-bottom:14px;">
            ${
              c.headingStyle === 'with-icon'
                ? '<svg style="width:18px;height:18px;color:#666;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>'
                : ''
            }
            <h4 style="margin:0;flex:1;font-size:${c.headingStyle === 'minimal' ? '13px' : '15px'};font-weight:${
                  c.headingStyle === 'minimal' ? '500' : '600'
                };color:${c.headingStyle === 'minimal' ? '#666' : '#1a1a2e'};${
                  c.headingStyle === 'minimal' ? 'text-transform:uppercase;letter-spacing:0.5px;' : ''
                }">${c.headingText || 'Related Searches'}</h4>
            ${
              showExpandButton
                ? `<a href="javascript:void(0)" class="quattr-expand-btn" data-expanded="false" style="font-size:13px;color:${pillColor};text-decoration:none;font-weight:500;cursor:pointer;">${viewMoreText}</a>`
                : ''
            }
          </div>`
              : ''
          }
          <div style="${layoutStyle}">
            ${pillsHtml}
          </div>
        </div>${expandScript}`;
    }

    default:
      return `<div style="padding:1rem;color:#64748b;">Preview for ${templateId}</div>`;
  }
}
